import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

export interface IpcTdAccountingParams {
  mailCategories: string[];
  accountingType: string;
  year: number;
  formatType: 'html' | 'pdf' | 'xls';
  direction?: string;
  designatedOperator?: string;
}

// New data structure for CN54bis CN56 Summary results
export interface IpcTdAccountingDataStructure {
  inboundFiles: InboundFile[];
  inboundSummaries: InboundSummary[];
  outboundFiles: OutboundFile[] | null;
  outboundSummaries: OutboundSummary[] | null;
}

export interface InboundFile {
  accountingQuarter: number;
  e53FileName: string;
  e53FileStatus: string;
  e55FileName: string | null;
  e55FileStatus: string | null;
}

export interface OutboundFile {
  // Add outbound file structure when available
  [key: string]: any;
}

export interface InboundSummary {
  mailCategory: string | null;
  cn54bisCn56SummaryRows: Cn54bisCn56SummaryRow[];
  sendingPpo: string;
  receivingPpo: string;
  year: number;
  key: string;
}

export interface OutboundSummary {
  // Add outbound summary structure when available
  [key: string]: any;
}

export interface Cn54bisCn56SummaryRow {
  cn54bisCn56SummaryRowType: string;
  cn54bisCn56SummaryCells: string[];
  cn54bisCn56SummaryAlerts: any[];
  originalItems: number | null;
  originalGrossWeight: number | null;
  originalGrossIpk: number | null;
  originalGross: number | null;
  bilateralItems: number | null;
  bilateralGrossWeight: number | null;
  bilateralGrossIpk: number | null;
  bilateralGross: number | null;
  bilateralItemsDeltaSign: number | null;
  bilateralGrossWeightDeltaSign: number | null;
  bilateralGrossIpkDeltaSign: number | null;
  bilateralGrossDeltaSign: number | null;
  bilateralItemsUnsigned: number | null;
  bilateralGrossWeightUnsigned: number | null;
}

// Legacy interfaces for backward compatibility (can be removed if not needed elsewhere)
export interface IpcTdAccountingItem {
  serviceType: string;
  accountingType: string;
  fileName: string;
  rows: IpcTdAccountingRow[];
}

export interface IpcTdAccountingRow {
  outboundOe: string;
  inboundOe: string;
  rcLabel: string;
  gross: number;
  tare: number;
  net: number;
  receivedReceptacleStratum: string;
  numberOfSamples: number;
  effectiveSamplingRateStratum: number;
  netIpk: number;
  pkg: number;
  gkg: number;
  ekg: number;
  pitems: number;
  gitems: number;
  eitems: number;
  jitems: number;
  zitems: number;
  jkg: number;
  zkg: number;
}

export interface IpcTdAccountingResponse {
  // For HTML format, contains the table data
  data?: IpcTdAccountingDataStructure;
  url?: string;
  content?: Blob;
  filename?: string; // Add filename field for PDF/XLS downloads
}

export function useIpcTdAccounting() {
  return useMutation<IpcTdAccountingResponse, Error, IpcTdAccountingParams>({
    mutationFn: async (params: IpcTdAccountingParams) => {
      const searchParams = new URLSearchParams();
      
      params.mailCategories.forEach(category => {
        searchParams.append('mailCategories', category);
      });
      
      searchParams.append('accountingType', params.accountingType);
      searchParams.append('year', params.year.toString());
      searchParams.append('formatType', params.formatType);
      if (params.direction) {
        searchParams.append('direction', params.direction);
      }
      if (params.designatedOperator) {
        searchParams.append('designatedOperator', params.designatedOperator);
      }

      const response = await axios.get(`/reports/cn54biscn56?${searchParams.toString()}`, {
        // HTML returns JSON with table data, PDF/XLS return binary files
        responseType: params.formatType === 'html' ? 'json' : 'blob',
      });

      // For HTML format, return the table data
      if (params.formatType === 'html') {
        return { data: response.data };
      }

      // For PDF/XLS, return the blob for file download
      let fileName: string | null = null;
      
      // Try to extract the filename from the Content-Disposition header
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }
      
      // If we couldn't get the filename from the header, generate one
      if (!fileName) {
        // Extract the current date in YYYYMMDD format for filename
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const timestamp = String(date.getHours()).padStart(2, '0') + 
                          String(date.getMinutes()).padStart(2, '0') + 
                          String(date.getSeconds()).padStart(2, '0');
        
        // Format filename with timestamp
        fileName = `CN54bis_CN56_Summary_${year}${month}${day}_${timestamp}.${params.formatType}`;
      }
      
      const mimeType = params.formatType === 'pdf' 
        ? 'application/pdf' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      return { 
        content: response.data,
        filename: fileName
      };
    },
  });
} 